/**
 * .tuMetrics {"tu1": 0.85, "tu2": 0.87, "tu3": 5, "notes": "App router; / is stream home; /2* redirects", "tuVersion": "2.5"}
 * .help "Defines app routes and lazy-loaded page components. Signed-in home is /. Legacy /2 paths redirect to the same desks and lenses."
 */
// src/pages/index.tsx
import React, { Suspense, lazy, useEffect } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useLocation,
  useParams,
  useSearchParams,
  Outlet,
} from 'react-router-dom';
import useUserStore from '@/store/userStore';
import useEntityViewerStore, { type ViewableEntityType } from '@/store/entityViewerStore';
import { supabase } from '../lib/supabase';
import { isBackplaneDeskLocked } from '@/features/stream/backplanePin';
import { twoHrefFromLens } from '@/features/stream/lens';
import { HOME_APP, canonicalFromLegacyTwo } from '@/constants/appHome';

// Lazy load all page components for code splitting
const InviteAcceptPage = lazy(() => import('./InviteAcceptPage'));
const AdminTwoPage = lazy(() => import('./AdminTwoPage'));
const AdminLegacyRedirect = lazy(async () => {
  const m = await import('./AdminTwoPage');
  return { default: m.AdminLegacyRedirect };
});
const SettingsIntegrationsRedirect = lazy(async () => {
  const m = await import('./AdminTwoPage');
  return { default: m.SettingsIntegrationsRedirect };
});
const HomePage = lazy(() => import('./HomePage'));
const LearnMorePage = lazy(() => import('./LearnMorePage'));
const LoginPage = lazy(() => import('./LoginPage'));
const SignupPage = lazy(() => import('./SignupPage'));
const PricingPage = lazy(() => import('./PricingPage'));
const ReportsTwoPage = lazy(() => import('./ReportsTwoPage'));
const StreamTwoPage = lazy(() => import('./StreamTwoPage'));
const TwoLayout = lazy(() => import('../features/layout/TwoLayout'));
const ComingSoonPage = lazy(() => import('./ComingSoonPage'));
const UpdatesTwoPage = lazy(() => import('./UpdatesTwoPage'));
const PrivacyPolicyPage = lazy(() => import('./PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./TermsOfServicePage'));
const MasterControlTwoPage = lazy(() => import('./MasterControlTwoPage'));
const CompleteSignupPage = lazy(() => import('./CompleteSignupPage'));
const UpdatePasswordPage = lazy(() => import('./UpdatePasswordPage'));
const ToznyCallbackPage = lazy(() => import('./ToznyCallbackPage'));
const ToznyStartPage = lazy(() => import('./ToznyStartPage'));
const MagicLinkPage = lazy(() => import('./MagicLinkPage'));
const OAuthCallbackPage = lazy(() => import('./OAuthCallbackPage'));
const GoogleWorkspaceCallbackPage = lazy(() => import('./GoogleWorkspaceCallbackPage'));
const GitHubAppCallbackPage = lazy(() => import('./GitHubAppCallbackPage'));

const PageLoader = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-transparent">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
    </div>
  );
};

const twoShell = (child: React.ReactNode) => (
  <ProtectedRoute>
    <Suspense fallback={<PageLoader />}>
      <TwoLayout>{child}</TwoLayout>
    </Suspense>
  </ProtectedRoute>
);

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, currentUser } = useUserStore();
  const location = useLocation();

  const inviteAccepted = (currentUser as { inviteAccepted?: boolean } | null)?.inviteAccepted === true;

  if (
    isAuthenticated &&
    inviteAccepted &&
    (location.pathname.startsWith('/update-password') ||
      location.pathname.startsWith('/complete-signup') ||
      location.pathname.startsWith('/accept-invite'))
  ) {
    return <Navigate to={HOME_APP} replace />;
  }

  if (
    isAuthenticated &&
    (currentUser as { inviteAccepted?: boolean } | null)?.inviteAccepted === false &&
    !location.pathname.startsWith('/accept-invite') &&
    !location.pathname.startsWith('/complete-signup') &&
    !location.pathname.startsWith('/update-password')
  ) {
    return <Navigate to="/accept-invite" replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isBackplaneDeskLocked(currentUser) && location.pathname !== '/') {
    return <Navigate to={HOME_APP} replace />;
  }

  return <>{children}</>;
};

/** Open a shared entity on the stream (EntityViewerHost lives on TwoLayout). */
const OpenEntityOnTwo: React.FC<{ type: ViewableEntityType; param: string }> = ({ type, param }) => {
  const params = useParams();
  const id = params[param];
  useEffect(() => {
    if (id) useEntityViewerStore.getState().viewEntity(type, id);
  }, [id, type]);
  return <Navigate to={HOME_APP} replace />;
};

const LegacyCardShareRedirect: React.FC = () => {
  const { cardId } = useParams<{ cardId: string }>();
  return <Navigate to={cardId ? `/card/${cardId}` : HOME_APP} replace />;
};

const LegacySprintShareRedirect: React.FC = () => {
  const { sprintId } = useParams<{ sprintId: string }>();
  return <Navigate to={sprintId ? `/sprint/${sprintId}` : HOME_APP} replace />;
};

const StreamDmToTwo: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  if (!userId) return <Navigate to={HOME_APP} replace />;
  return <Navigate to={twoHrefFromLens({ kind: 'room', streamType: 'dm', id: userId })} replace />;
};

const StreamTeamToTwo: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  if (!teamId) return <Navigate to={HOME_APP} replace />;
  return <Navigate to={twoHrefFromLens({ kind: 'room', streamType: 'team', id: teamId })} replace />;
};

const StreamStandupToTwo: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  if (!sessionId) return <Navigate to={HOME_APP} replace />;
  return <Navigate to={twoHrefFromLens({ kind: 'room', streamType: 'standup', id: sessionId })} replace />;
};

const StreamServiceToTwo: React.FC = () => {
  const { serviceType } = useParams<{ serviceType: string }>();
  if (!serviceType) return <Navigate to={HOME_APP} replace />;
  return <Navigate to={twoHrefFromLens({ kind: 'service', id: serviceType })} replace />;
};


const LegacyTwoRedirect: React.FC = () => {
  const { pathname, search, hash } = useLocation();
  const next = canonicalFromLegacyTwo(pathname);
  return <Navigate to={`${next}${search}${hash}`} replace />;
};

const AdminEntry: React.FC = () => {
  const [params] = useSearchParams();
  if (params.has('tab') && !params.get('section')) {
    return (
      <ProtectedRoute>
        <Suspense fallback={<PageLoader />}>
          <AdminLegacyRedirect />
        </Suspense>
      </ProtectedRoute>
    );
  }
  return twoShell(<AdminTwoPage />);
};

function useAuthFlags() {
  const location = useLocation();
  const hash = location.hash || '';
  const search = location.search || '';

  const hasIdToken = hash.includes('id_token=');
  const isMagicLink =
    hash.includes('type=magiclink') ||
    search.includes('type=magiclink') ||
    (hash.includes('access_token=') && hash.includes('refresh_token='));

  const isOAuthCodeFlow = search.includes('code=');

  return { hasIdToken, isMagicLink, isOAuthCodeFlow };
}

const RootRouteElement: React.FC = () => {
  const { isAuthenticated } = useUserStore();
  const { hasIdToken, isMagicLink, isOAuthCodeFlow } = useAuthFlags();

  if (isOAuthCodeFlow) {
    return (
      <Suspense fallback={<PageLoader />}>
        <OAuthCallbackPage />
      </Suspense>
    );
  }

  if (isMagicLink) {
    return (
      <Suspense fallback={<PageLoader />}>
        <MagicLinkPage />
      </Suspense>
    );
  }

  if (hasIdToken) {
    return (
      <Suspense fallback={<PageLoader />}>
        <ToznyCallbackPage />
      </Suspense>
    );
  }

  return isAuthenticated ? (
    twoShell(<StreamTwoPage />)
  ) : (
    <Suspense fallback={<PageLoader />}>
      <HomePage />
    </Suspense>
  );
};

const LoginRouteElement: React.FC = () => {
  const { isAuthenticated } = useUserStore();
  const { hasIdToken, isMagicLink, isOAuthCodeFlow } = useAuthFlags();

  if (isOAuthCodeFlow) {
    return (
      <Suspense fallback={<PageLoader />}>
        <OAuthCallbackPage />
      </Suspense>
    );
  }

  if (isMagicLink) {
    return (
      <Suspense fallback={<PageLoader />}>
        <MagicLinkPage />
      </Suspense>
    );
  }

  if (hasIdToken) {
    return (
      <Suspense fallback={<PageLoader />}>
        <ToznyCallbackPage />
      </Suspense>
    );
  }

  return isAuthenticated ? (
    <Navigate to={HOME_APP} replace />
  ) : (
    <Suspense fallback={<PageLoader />}>
      <LoginPage />
    </Suspense>
  );
};

const TenantLayout: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slug) return;

      const { data } = await supabase
        .from('companies')
        .select('id, slug')
        .ilike('slug', slug)
        .single();

      if (!cancelled && data?.id) {
        try {
          const useCompanyStore = (await import('@/store/companyStore')).default;
          const companyStore = useCompanyStore.getState();
          if (companyStore?.initializeCompanySettings) {
            await companyStore.initializeCompanySettings(data.id);
          }
        } catch (e) {
          console.warn('TenantLayout: companyStore not available or failed to init', e);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return <Outlet />;
};

const Pages: React.FC = () => {
  const { isLoadingAuth, isAuthenticated, coreDataReady, currentUser } = useUserStore();
  const invitePending = (currentUser as { inviteAccepted?: boolean } | null)?.inviteAccepted === false;

  if (isLoadingAuth || (isAuthenticated && !invitePending && !coreDataReady)) {
    return <PageLoader />;
  }

  const router = createBrowserRouter([
    { path: '/', element: <RootRouteElement /> },

    {
      path: '/companies/:slug',
      element: <TenantLayout />,
      children: [
        {
          path: 'auth/callback',
          element: (
            <Suspense fallback={<PageLoader />}>
              <OAuthCallbackPage />
            </Suspense>
          ),
        },
        {
          path: 'realms/:realm/*',
          element: (
            <ProtectedRoute>
              <Navigate to={HOME_APP} replace />
            </ProtectedRoute>
          ),
        },
        {
          path: 'realms/:realm/flow',
          element: (
            <ProtectedRoute>
              <Navigate to={twoHrefFromLens({ kind: 'mine' })} replace />
            </ProtectedRoute>
          ),
        },
      ],
    },

    // --- Legacy 1.0 paths → 2.0 lenses / desks ---
    {
      path: '/flow',
      element: (
        <ProtectedRoute>
          <Navigate to={twoHrefFromLens({ kind: 'mine' })} replace />
        </ProtectedRoute>
      ),
    },
    {
      path: '/cards',
      element: (
        <ProtectedRoute>
          <Navigate to={twoHrefFromLens({ kind: 'inventory', id: 'cards' })} replace />
        </ProtectedRoute>
      ),
    },
    {
      path: '/sprints',
      element: (
        <ProtectedRoute>
          <Navigate to={twoHrefFromLens({ kind: 'inventory', id: 'sprints' })} replace />
        </ProtectedRoute>
      ),
    },
    {
      path: '/sprint/:sprintId',
      element: (
        <ProtectedRoute>
          <OpenEntityOnTwo type="sprint" param="sprintId" />
        </ProtectedRoute>
      ),
    },
    {
      path: '/sprints/:sprintId',
      element: <LegacySprintShareRedirect />,
    },
    {
      path: '/projects',
      element: (
        <ProtectedRoute>
          <Navigate to={twoHrefFromLens({ kind: 'inventory', id: 'projects' })} replace />
        </ProtectedRoute>
      ),
    },
    {
      path: '/directory',
      element: (
        <ProtectedRoute>
          <Navigate to={HOME_APP} replace />
        </ProtectedRoute>
      ),
    },
    {
      path: '/roadmaps',
      element: (
        <ProtectedRoute>
          <Navigate to={twoHrefFromLens({ kind: 'inventory', id: 'roadmaps' })} replace />
        </ProtectedRoute>
      ),
    },
    {
      path: '/roadmap/:roadmapId',
      element: (
        <ProtectedRoute>
          <OpenEntityOnTwo type="roadmap" param="roadmapId" />
        </ProtectedRoute>
      ),
    },
    {
      path: '/features',
      element: (
        <ProtectedRoute>
          <Navigate to={twoHrefFromLens({ kind: 'inventory', id: 'features' })} replace />
        </ProtectedRoute>
      ),
    },
    {
      path: '/plans',
      element: (
        <ProtectedRoute>
          <Navigate to={twoHrefFromLens({ kind: 'inventory', id: 'plans' })} replace />
        </ProtectedRoute>
      ),
    },
    {
      path: '/devex',
      element: (
        <ProtectedRoute>
          <Navigate to={HOME_APP} replace />
        </ProtectedRoute>
      ),
    },
    {
      path: '/resources',
      element: (
        <ProtectedRoute>
          <Navigate to={HOME_APP} replace />
        </ProtectedRoute>
      ),
    },
    {
      path: '/admin',
      element: <AdminEntry />,
    },
    {
      path: '/settings/integrations',
      element: (
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <SettingsIntegrationsRedirect />
          </Suspense>
        </ProtectedRoute>
      ),
    },
    {
      path: '/master-control',
      element: twoShell(<MasterControlTwoPage />),
    },
    {
      path: '/reports',
      element: twoShell(<ReportsTwoPage />),
    },
    {
      path: '/updates',
      element: twoShell(<UpdatesTwoPage />),
    },
    {
      path: '/dashboard',
      element: (
        <ProtectedRoute>
          <Navigate to="/reports" replace />
        </ProtectedRoute>
      ),
    },
    {
      path: '/help',
      element: (
        <ProtectedRoute>
          <Navigate to="/updates" replace />
        </ProtectedRoute>
      ),
    },
    // Legacy /2 addresses → canonical / and desks (bookmarks + push payloads).
    {
      path: '/2/*',
      element: (
        <ProtectedRoute>
          <LegacyTwoRedirect />
        </ProtectedRoute>
      ),
    },
    {
      path: '/2',
      element: (
        <ProtectedRoute>
          <LegacyTwoRedirect />
        </ProtectedRoute>
      ),
    },
    {
      path: '/card/:cardId',
      element: (
        <ProtectedRoute>
          <OpenEntityOnTwo type="card" param="cardId" />
        </ProtectedRoute>
      ),
    },
    {
      path: '/cards/:cardId',
      element: <LegacyCardShareRedirect />,
    },
    {
      path: '/streams/dm/:userId',
      element: (
        <ProtectedRoute>
          <StreamDmToTwo />
        </ProtectedRoute>
      ),
    },
    {
      path: '/streams/company',
      element: (
        <ProtectedRoute>
          <Navigate to={twoHrefFromLens({ kind: 'room', streamType: 'company', id: '' })} replace />
        </ProtectedRoute>
      ),
    },
    {
      path: '/streams/team/:teamId',
      element: (
        <ProtectedRoute>
          <StreamTeamToTwo />
        </ProtectedRoute>
      ),
    },
    {
      path: '/streams/standup/:sessionId',
      element: (
        <ProtectedRoute>
          <StreamStandupToTwo />
        </ProtectedRoute>
      ),
    },
    {
      path: '/streams/service/:serviceType',
      element: (
        <ProtectedRoute>
          <StreamServiceToTwo />
        </ProtectedRoute>
      ),
    },
    {
      path: '/share-finder',
      element: (
        <ProtectedRoute>
          <Navigate to={twoHrefFromLens({ kind: 'shares' })} replace />
        </ProtectedRoute>
      ),
    },
    {
      path: '/session-finder',
      element: (
        <ProtectedRoute>
          <Navigate to={twoHrefFromLens({ kind: 'inventory', id: 'meetings' })} replace />
        </ProtectedRoute>
      ),
    },

    // --- PUBLIC & AUTH-FLOW ROUTES ---
    { path: '/login', element: <LoginRouteElement /> },
    {
      path: '/signup',
      element: useUserStore.getState().isAuthenticated ? (
        <Navigate to={HOME_APP} replace />
      ) : (
        <Suspense fallback={<PageLoader />}>
          <SignupPage />
        </Suspense>
      ),
    },
    {
      path: '/accept-invite',
      element: (
        <Suspense fallback={<PageLoader />}>
          <InviteAcceptPage />
        </Suspense>
      ),
    },
    {
      path: '/pricing',
      element: useUserStore.getState().isAuthenticated ? (
        <Navigate to={HOME_APP} replace />
      ) : (
        <Suspense fallback={<PageLoader />}>
          <PricingPage />
        </Suspense>
      ),
    },
    {
      path: '/learn-more',
      element: useUserStore.getState().isAuthenticated ? (
        <Navigate to={HOME_APP} replace />
      ) : (
        <Suspense fallback={<PageLoader />}>
          <LearnMorePage />
        </Suspense>
      ),
    },
    {
      path: '/complete-signup',
      element: (
        <Suspense fallback={<PageLoader />}>
          <CompleteSignupPage />
        </Suspense>
      ),
    },
    {
      path: '/update-password',
      element: (
        <Suspense fallback={<PageLoader />}>
          <UpdatePasswordPage />
        </Suspense>
      ),
    },
    {
      path: '/comingsoon',
      element: (
        <Suspense fallback={<PageLoader />}>
          <ComingSoonPage />
        </Suspense>
      ),
    },
    {
      path: '/tozny-callback',
      element: (
        <Suspense fallback={<PageLoader />}>
          <ToznyCallbackPage />
        </Suspense>
      ),
    },
    {
      path: '/toz',
      element: (
        <Suspense fallback={<PageLoader />}>
          <ToznyStartPage />
        </Suspense>
      ),
    },
    {
      path: '/oauth/callback',
      element: (
        <Suspense fallback={<PageLoader />}>
          <OAuthCallbackPage />
        </Suspense>
      ),
    },
    {
      path: '/google-workspace/callback',
      element: (
        <Suspense fallback={<PageLoader />}>
          <GoogleWorkspaceCallbackPage />
        </Suspense>
      ),
    },
    {
      path: '/oauth/github-app/callback',
      element: (
        <Suspense fallback={<PageLoader />}>
          <GitHubAppCallbackPage />
        </Suspense>
      ),
    },
    {
      path: '/privacy',
      element: (
        <Suspense fallback={<PageLoader />}>
          <PrivacyPolicyPage />
        </Suspense>
      ),
    },
    {
      path: '/terms',
      element: (
        <Suspense fallback={<PageLoader />}>
          <TermsOfServicePage />
        </Suspense>
      ),
    },
  ]);

  return <RouterProvider router={router} />;
};

export default Pages;
